import { useEffect, useMemo, useReducer, useState, useRef } from "react";
import GTable from "../../design/components/GTable";
import Button from "../../design/Button";
import GMultiSelect from "../../design/components/GMultiSelect.jsx";
import { Filters } from "../../dashboard/Filters";
import AnimationPage from "../../shared/animation-page/AnimationPage";
import { JService } from "../../api-service/ApiService";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import colors from "../../design/colors";
import styles from "../../design/styles";
import {
  filtersReducer,
  formatDate,
  formatTime,
  getOrgURLParam,
} from "../../../utility-functions";
import classes from "./SmsLogs.module.css";

// # assets
import {
  callIcon,
  callLogsTableIncomingArrow,
  callLogsTableOutgoingArrow,
  errorIcon,
  messageIcon,
} from "../../../utility-functions/assetLoader";
import { enqueueSnackbar } from "notistack";

const MESSAGE_TYPES_MAPPING = {
  inbound: 1,
  outbound: 2,
  unread: 3,
};

const initialState = {
  users: { all: [], selected: [] },
  teams: { all: [], selected: [] },
  tags: { all: [], selected: [] },
  messageType: {
    all: [
      { id: "inbound", name: "Inbound" },
      { id: "outbound", name: "Outbound" },
      { id: "unread", name: "Unread" },
    ],
    selected: [],
  },
  searchText: "",
  searchFields: [],
};

const filtersList = [
  { name: "Message type", isSearch: false, payloadFor: "messageType" },
  {
    name: "Team",
    isSearch: true,
    payloadFor: "teams",
    permission: "view teams",
  },
  {
    name: "Users",
    isSearch: true,
    payloadFor: "users",
    permission: "view users",
  },
  { name: "Tags", isSearch: true, payloadFor: "tags" },
];

const formatCalendarDate = (date) => {
  return `${new Date(date).getFullYear()}-${(
    "0" +
    (new Date(date).getMonth() + 1)
  ).slice(-2)}-${("0" + new Date(date).getDate()).slice(-2)}`;
};
const localStyles = {
  spanNoOverFlowStyleForLogsContact: {
    display: "inline-block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    width: "160px",
  },
};

const SmsLogs = ({
  users,
  teams,
  allTags,
  id,
  fetchData,
  tagDetailsSet,
  setTagDetailsSet,
  handleOpenConversation,
  setMessageHistoryModal,
  messagingEnabled,
  initiateCall,
}) => {
  const API_BASE_URL = process.env.REACT_APP_BETA;

  const [availableData, dispatchAvailableData] = useReducer(
    filtersReducer,
    initialState,
  );

  const navigate = useNavigate();

  const reduxAccount = useSelector((state) => state?.account?.value);
  const reduxCredits = useSelector((state) => state?.account?.value?.credits);
  const maskNumbers = useSelector((state) => state?.auth?.maskNumbers);
  const { canSendSms, canMakeCalls } = useSelector(
    (state) => state?.auth?.permissions,
  );

  const [gTablePageNumber, setGTablePageNumber] = useState(0);
  const [showLoader, setShowLoader] = useState(false);
  const [pageData, setPageData] = useState({ rowCount: 0, pageSize: 10 });
  const [data, setData] = useState([]);
  const [rowsTags, setRowsTags] = useState([]);
  const [calendar, setCalendar] = useState({
    date_start: formatCalendarDate(Date.now() - 86400000), //  2 days data
    date_end: formatCalendarDate(Date.now()),
  });
  const [showTable, setShowTable] = useState(true);

  const selectedRowTagRef = useRef([]);

  const teamsIncludingCurrentUser = useMemo(() => {
    const userId = reduxAccount?.data?.profile?.user;
    return teams
      ?.filter((team) => team.users.includes(userId))
      ?.map((team) => team.id);
  }, [teams, reduxAccount?.data?.profile?.user]);

  const handlePageChange = (newPage, pageDirection) => {
    if (pageDirection && newPage > -1) {
      const _type = "fetch".concat(pageDirection);
      fetchMessageLogs(_type, newPage + 1, pageData.pageSize);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setGTablePageNumber(0);
    fetchMessageLogs("page-size-change", 1, newSize);
  };

  const fetchMessageLogs = (type, page_number, page_size, calendarObj = {}) => {
    setShowLoader(true);

    const date_start = calendarObj.date_start || calendar.date_start;
    const date_end = calendarObj.date_end || calendar.date_end;

    setShowTable(false);

    let url = `/api/v1/core/retrieve-messages/${getOrgURLParam()}?webapp=True&page=${page_number}&page_size=${page_size}&date_start=${date_start}&date_end=${date_end}`;
    if (availableData.users?.selected?.length)
      url += `&users=${JSON.stringify(availableData?.users?.selected)}`;

    if (Object.keys(availableData?.teams?.selected)?.length)
      url += `&teams=${JSON.stringify(Object.keys(availableData.teams.selected)?.map((at) => Number(at)))}`;

    if (availableData.tags?.selected?.length)
      url += `&tags=${JSON.stringify(availableData.tags.selected)}`;

    if (availableData.messageType.selected?.length) {
      let temp = null;
      try {
        temp = availableData.messageType.selected?.map(
          (s) => MESSAGE_TYPES_MAPPING[s],
        );
      } catch (er) {
        temp = null;
      }
      if (temp instanceof Array)
        url += `&message_types=${JSON.stringify(temp)}`;
    }

    if (availableData?.searchText?.trim()?.length !== 0) {
      url += `&search=${encodeURIComponent(availableData?.searchText?.trim())}`;
    }

    if (type === "filters-changed" && gTablePageNumber > 0) {
      setGTablePageNumber(0);
    }
    if (!showLoader && showTable) setShowLoader(true);
    JService.get(url)
      .then((res) => {
        if (!res.success)
          throw new Error(res.message || "Failed to retrieve message logs");

        setData(res?.data?.results)
        setPageData({rowCount:res?.data?.count || 0, pageSize:page_size})
      })
      .catch((err) => {
        console.log(err);
        enqueueSnackbar(err?.message, { variant: "error" });
      })
      .finally(() => {
        setShowLoader(false);
        setShowTable(true);
      });
  };

  const handleFilterApply = (filterName, val) => {
    if (filterName === "Date") {
      setCalendar(val);

      dispatchAvailableData({ type: "clearAllFilters", payload: "" });
      setGTablePageNumber(0);
    } else console.log(filterName, val);
  };

  // onSearch is called from GTable
  const onSearch = (searchText) => {
    // used for reset when cleared and value of textField is null
    if (window.location.pathname?.includes("messages"))
      window.location.replace("/activity-logs/messages");
    else navigate(0);
  };

  const customDownloadHandler = (e) => {
    let url = `/api/v1/core/send-sms-messages/${getOrgURLParam()}?webapp=true&date_start=${calendar?.date_start}&date_end=${calendar?.date_end}`;

    if (availableData?.users?.selected?.length)
      url += `&users=${JSON.stringify(availableData?.users?.selected)}`;

    if (Object.keys(availableData?.teams?.selected)?.length)
      url += `&teams=${JSON.stringify(Object.keys(availableData.teams.selected)?.map((at) => Number(at)))}`;

    if (availableData?.tags?.selected?.length)
      url += `&tags=${JSON.stringify(availableData?.tags?.selected)}`;

    if (availableData.messageType.selected?.length) {
      let temp = null;
      try {
        temp = availableData.messageType.selected?.map(
          (s) => MESSAGE_TYPES_MAPPING[s],
        );
      } catch (er) {
        temp = null;
      }
      if (temp instanceof Array)
        url += `&message_types=${JSON.stringify(temp)}`;
    }

    if (availableData?.searchText?.trim()?.length !== 0) {
      url += `&search=${encodeURIComponent(availableData?.searchText?.trim())}`;
    }

    JService.get(url)
      .then((res) => {
        if (!res?.success)
          throw new Error(
            res?.detail || "Error occurred while sending report to inbox",
          );

        enqueueSnackbar(
          res?.message || "You will receive the call logs report in your inbox",
          { variant: "success" },
        );
      })
      .catch((err) => {
        enqueueSnackbar(err?.message, { variant: "error" });
      });
  };

  function handleUpdateTags(payload = [], message_id = -1) {
    JService.patch(
      API_BASE_URL + "/api/v1/core/update-message-record/",
      JSON.stringify({ message_id, tag_ids: [...payload] })
    )
      .then((res) => {
        if (!res?.success) throw new Error(res?.message || "Failed to add tag");
       setData(prev=>{
        let temp=[...prev];
        const newData=temp.map((elem)=>{
          if(elem.id===message_id){
            return {
              ...elem, tags:[...res?.data?.tags]
            }
          }
          else{
            return elem
          }
        })
        return newData
        
       })
       enqueueSnackbar(res?.message, {variant:'success'})
       setRowsTags([]);
      })
      .catch((err) => {
        enqueueSnackbar(err?.message, { variant: "error" });
      })
      .finally(() => {
        if (showLoader) setShowLoader(false);
      });
  }

  const onRowClick = (params) => {
    const candidateDetails = {
      name: params?.row?.contact_name,
      phone_number: params?.row?.contact_number,
    };
    const currentUserEmail = reduxAccount?.data?.email;
    const isCreator =
      currentUserEmail === params?.row?.creator ||
      teamsIncludingCurrentUser.includes(params?.row?.team?.id);

    setMessageHistoryModal({
      visible: true,
      id: params?.row?.id,
      candidateDetails,
      viewOnly: !isCreator || !canSendSms,
      viewedMsgId: params?.row?.id,
    });
  };

  const cols = useMemo(() => {
    return [
      {
        field: "logsContact",
        headerClassName: "user-table-header-user-name",
        headerName: "Contact",
        minWidth: 285,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              paddingLeft: !params?.row?.read_status
                ? "4px !important"
                : "14px !important",
              color: colors.nc_gray_900,
              ...styles.regular_font,
              ...styles.t7,
            }}
          >
            <div style={{ display: "flex", alignContent: "flex-start" }}>
              {!params?.row?.read_status && (
                <span className={classes.unreadCircle}></span>
              )}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  maxWidth: "152px",
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    marginBottom: "4px",
                    ...localStyles.spanNoOverFlowStyleForLogsContact,
                  }}
                >
                  {params?.row?.contact_name}
                </span>

                {maskNumbers &&
                params?.row?.contact_name?.trim()?.length ? null : (
                  <span
                    className="nc-gray-600 t7"
                    style={{ ...localStyles.spanNoOverFlowStyleForLogsContact }}
                  >
                    {params?.row?.contact_number}
                  </span>
                )}
              </Box>
            </div>
            {reduxAccount?.data?.license &&
              reduxAccount?.data?.license !== "Viewer" && (
                <div className="table-cell-edit">
                  <div>
                    {canMakeCalls && (
                      <span
                        className={classes.contactIcons}
                        onClick={(e) =>
                          initiateCall(e, params?.row?.contact_number)
                        }
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {callIcon()}
                      </span>
                    )}
                    {canSendSms && (
                      <span
                        onClick={(e) =>
                          messagingEnabled &&
                          !params?.row?.contact_number?.startsWith("+91")
                            ? handleOpenConversation(e, params, "sms-logs")
                            : null
                        }
                        disabled={
                          !messagingEnabled ||
                          params?.row?.contact_number?.startsWith("+91")
                        }
                        className={`${classes.contactIcons} ${!messagingEnabled || params?.row?.contact_number?.startsWith("+91") ? classes.messageDisabled : ""}`}
                      >
                        {messageIcon()}
                      </span>
                    )}
                  </div>
                </div>
              )}
          </Box>
        ),
      },
      {
        field: "logsContactedOn",
        headerClassName: "user-table-header-user-name",
        headerName: "Contacted on",
        // flex: 1.2,
        minWidth: 220,
        renderCell: (params) => {
          const arrowIdentifier =
            params?.row?.direction === "OUTBOUND"
              ? params?.row?.status === "failed"
                ? "errorIcon"
                : "arrowOut"
              : params?.row?.direction === "INBOUND"
                ? params?.row?.status === "delivered"
                  ? "arrowIn"
                  : "errorIcon"
                : null;

          let arrow = null;

          if (arrowIdentifier === "arrowIn")
            arrow = callLogsTableIncomingArrow(colors.ic_green_400);
          else if (arrowIdentifier === "arrowOut")
            arrow = callLogsTableOutgoingArrow(colors.ic_green_400);
          else if (arrowIdentifier === "errorIcon")
            arrow = errorIcon(colors.ic_red_400);
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",

                paddingLeft: "14px",
                color: colors.nc_gray_900,
                ...styles.regular_font,
                ...styles.t7,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  marginBottom: "4px",
                  alignItems: "center",
                }}
              >
                {arrowIdentifier === "arrowIn" && (
                  <div
                    style={{
                      transform: "rotate(0deg)",
                      display: "inline-block",
                      marginTop: "8px",
                    }}
                  >
                    {arrow}
                  </div>
                )}

                {arrowIdentifier !== "arrowIn" && (
                  <div
                    style={{
                      transform: "rotate(0deg)",
                      display: "inline-block",
                    }}
                  >
                    {arrow}
                  </div>
                )}

                <span style={{ marginLeft: "12px", display: "inline-block" }}>
                  {formatDate(params?.row?.timestamp)}
                </span>
              </Box>

              <p style={{ paddingLeft: "28px" }} className="nc-gray-600 t7">
                &nbsp;{formatTime(params?.row?.timestamp)}
              </p>
            </Box>
          );
        },
      },
      {
        field: "logsUser",
        headerClassName: "user-table-header-user-name",
        headerName: "User",
        minWidth: 220,
        renderCell: (params) => {
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",

                paddingLeft: "12px",
                color: colors.nc_gray_900,
                ...styles.regular_font,
                ...styles.t7,
              }}
            >
              <Box
                sx={{
                  marginBottom: "4px",
                  alignItems: "center",
                  marginLeft: "12px",
                }}
              >
                <p>{params?.row?.creator_name}</p>
                <p className="nc-gray-600 t7">{params?.row?.user_number}</p>
              </Box>
            </Box>
          );
        },
      },
      {
        field: "logsMessage",
        headerName: "Message",
        minWidth: 200,
        renderCell: (params) => (
          <Box sx={{ padding: "0px" }}>
            <Button
              onClick={() => onRowClick(params)}
              buttonText="View"
              variant="text"
              hierarchy="white"
              icon={null}
              isLoading={false}
              disabled={false}
              styleOverride={{
                textDecoration: "none",
                fontSize: "14px",
                padding: "0",
                "&:hover": {
                  textDecoration: "underline",
                  backgroundColor: "transparent",
                },
                transition: "all 0.3s ease-out",
              }}
            />
          </Box>
        ),
      },
      {
        field: "logsCost",
        headerName: `Cost (${reduxCredits?.currency})`,
        minWidth: 220,
        renderCell: (params) => {
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",

                  paddingLeft: "14px",
                  color: colors.nc_gray_900,
                  ...styles.regular_font,
                  ...styles.t7,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    marginBottom: "4px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{ marginLeft: "12px", display: "inline-block" }}
                  >
                    {!isNaN(params?.row?.cost)?Number(params?.row?.cost).toFixed(3):'0.000'}
                  </span>
                </Box>
              </Box>
            );
          },

        },
        {
          field: "logsTeam",
          headerName: "Team",
          minWidth: 220,
          renderCell: (params) => {
            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  paddingLeft: "12px",
                  color: colors.nc_gray_900,
                  ...styles.regular_font,
                  ...styles.t7,
                }}
              >
                        <p>{params?.row?.team?.name || "-"}</p>
              </Box>
            );
          },
        },
        {
          field: "logsTags",
          headerName: "Tags",
          headerClassName: "logs-table-header-call-reason-outcome",
          minWidth: 300,
          renderCell: (params) => {
            const tagIdArray = params?.row?.tags?.map((tag) => tag?.id);
            return (
              <GMultiSelect
                label=""
                value={tagIdArray}
                defaultValue="+ Add tags"
                handleChange={(val) => handleUpdateTags(
                    val,
                    params?.row?.id
                )}
                handleDeleteChip={(removedValue) => handleUpdateTags(tagIdArray.filter(id => id !== removedValue), params?.row?.id)}
                options={allTags?.map(tag => ({value: tag.id, displayValue: tag.name}))}
                selectStyles={{maxWidth: '270px'}}
              />
            );
          },
        },
      ]
    },[canSendSms, canMakeCalls, maskNumbers, messagingEnabled])

  useEffect(() => {
    if (allTags && Array.isArray(allTags) && allTags.length > 0) {
      dispatchAvailableData({
        type: "setPayloadToAllOfType",
        payload: { payloadFor: "tags", payload: allTags },
      });
    }
  }, [allTags]);

  useEffect(() => {
    if (users && Array.isArray(users) && users.length > 0) {
      dispatchAvailableData({
        type: "setPayloadToAllOfType",
        payload: {
          payloadFor: "users",
          payload: users?.map((rd) => ({
            name: (rd?.first_name + " " + rd?.last_name)?.trim() || rd?.email,
            id: rd?.id,
          })),
        },
      });
    }
  }, [users]);

  useEffect(() => {
    if (teams && Array.isArray(teams) && teams.length > 0) {
      const teamUserMapping = {};
      data?.forEach((u) => {
        teamUserMapping[u.id] = u.users || [];
      });
      dispatchAvailableData({
        type: "setPayloadToAllOfType",
        payload: {
          payloadFor: "teams",
          payload: teams?.map((u) => ({
            name: u.name,
            id: u.id,
            // users: u.users || [],
          })),
          extraPayload: teamUserMapping,
        },
      });
    }
  }, [teams]);

  useEffect(() => {
    fetchMessageLogs("filters-changed", 1, 10);
  }, [
    availableData?.users?.selected,
    availableData?.teams?.selected,
    availableData?.messageType?.selected,
    availableData?.tags?.selected,
    availableData?.searchText,
  ]);

  return (
    <div className="screen-container call-logs-override-screen-container">
      <Filters
        onApply={handleFilterApply}
        fromScreen="sms-logs"
        availableData={availableData}
        dispatchAvailableData={dispatchAvailableData}
        filtersList={filtersList}
        defaultDateRange={1}
      />
      {showTable ? (
        <GTable
          className="call-logs-gtable"
          leftHeader={{
            isSearch: true,
            filters: [],
          }}
          searchFields={["logsContact"]}
          dispatchAvailableData={dispatchAvailableData}
          availableData={availableData}
          fromScreen="sms-logs"
          refreshHandler={null}
          rightHeader={{ isRefresh: true, isDownload: true }}
          download={{
            isCustom: true,
            customDownloadHandler,
            data: {},
            body: null,
            filename:
              "frejun-sms-logs-" +
              new Date().toLocaleString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              }),
          }}
          rows={data}
          columns={cols}
          useMuiTable={true}
          isLoading={showLoader}
          getTableRowClassName="logs-table-row" // to be revised
          additionalProps={{ bgHover: true }}
          onRowClick={() => null}
          onSearch={onSearch}
          combineSearchAndFilters={() => null}
          rowCount={pageData.rowCount || 0}
          handlePageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          rowsPerPage={pageData.pageSize || 10}
          gTablePageNumber={gTablePageNumber}
          setGTablePageNumber={setGTablePageNumber}
          paginationMode="server"
        />
      ) : (
        <AnimationPage />
      )}
    </div>
  );
};

export default SmsLogs;
